/**
 * MaintenanceSettings — admin-gränssnitt för underhållsfönstret.
 *
 * Renderas bara för ADMIN (anroparen kollar rollen). Backend gör samma
 * kontroll oberoende av detta — UI-döljning är bekvämlighet, inte skydd.
 *
 * TIDER: fälten är svensk lokaltid. Datetime-local-inputen har inget
 * tidszonsbegrepp, vilket passar exakt: strängen "2026-07-24T12:00" skickas
 * rå till backend, som tolkar den mot Europe/Stockholm. Ingen konvertering
 * sker i webbläsaren — annars hade användarens egen tidszon kunnat smyga in.
 *
 * De två fönstren är OBEROENDE. Bannern visas typiskt dagar innan
 * blockeringen slår till, så förvarningen hinner nå fram.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Input, Popconfirm, message } from "antd";
import Request from "../../utils/request";

const { TextArea } = Input;

/** ISO-instant -> "YYYY-MM-DDTHH:MM" i svensk tid, för datetime-local-fälten. */
const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const p = new Intl.DateTimeFormat("sv-SE", {
    timeZone: "Europe/Stockholm",
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  }).formatToParts(d);
  const g = (t) => p.find((x) => x.type === t).value;
  return `${g("year")}-${g("month")}-${g("day")}T${g("hour")}:${g("minute")}`;
};

const Field = ({ label, value, onChange, hint }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
    <label style={{ fontWeight: 600, fontSize: 12 }}>{label}</label>
    <input
      type="datetime-local"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{ padding: "4px 8px", border: "1px solid #d9d9d9", borderRadius: 2, height: 32 }}
    />
    {hint ? <span style={{ fontSize: 11, color: "#888" }}>{hint}</span> : null}
  </div>
);

const MaintenanceSettings = () => {
  const [form, setForm] = useState({
    message: "", bannerStart: "", bannerEnd: "", blockStart: "", blockEnd: "",
  });
  const [status, setStatus] = useState(null);
  const [health, setHealth] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [win, st, hp] = await Promise.all([
      Request("/maintenance", { method: "GET" }),
      Request("/maintenance/status", { method: "GET" }),
      Request("/maintenance/geocode-health", { method: "GET" }),
    ]);
    if (hp.response) setHealth(hp.response);
    if (win.response) {
      const w = win.response;
      setForm({
        message: w.message || "",
        bannerStart: toLocalInput(w.bannerStart),
        bannerEnd: toLocalInput(w.bannerEnd),
        blockStart: toLocalInput(w.blockStart),
        blockEnd: toLocalInput(w.blockEnd),
      });
    }
    if (st.response) setStatus(st.response);
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    for (const [k, label] of [
      ["bannerStart", "Banner från"], ["bannerEnd", "Banner till"],
      ["blockStart", "Blockering från"], ["blockEnd", "Blockering till"],
    ]) {
      if (!form[k]) { message.error(`${label} måste fyllas i.`); return; }
    }
    setSaving(true);
    const { response, error } = await Request("/maintenance", {
      method: "PUT",
      data: form,
    });
    setSaving(false);
    if (error) {
      message.error(error.message || "Kunde inte spara underhållsfönstret.");
      return;
    }
    setStatus(response);
    message.success("Underhållsfönstret sparat.");
  };

  const cancelNow = async () => {
    const { response, error } = await Request("/maintenance", { method: "DELETE" });
    if (error) { message.error(error.message || "Kunde inte avsluta."); return; }
    setStatus(response);
    message.success("Underhållet avslutat. Banner och blockering är av.");
  };

  return (
    <div style={{ marginTop: 32, maxWidth: 720 }}>
      <div style={{ fontWeight: 600, fontSize: "1.5em", marginBottom: 8 }}>
        Planerat underhåll
      </div>

      {status ? (
        <Alert
          style={{ marginBottom: 16 }}
          type={status.blockActive ? "error" : status.bannerActive ? "info" : "success"}
          message={
            status.blockActive
              ? `Blockering AKTIV — nya importer stoppas till ${status.blockEndLocal}.`
              : status.bannerActive
              ? "Banner visas för användarna. Importer är inte blockerade än."
              : "Inget aktivt underhåll. Varken banner eller blockering visas."
          }
        />
      ) : null}

      {/*
        Fastnade geokodningsjobb. Workerns kö ligger i minnet och överlever inte
        en omstart — startas backend om mitt i en import står adresserna kvar
        med geocoding=true utan att något jobb finns. Visas här för att göra ett
        annars helt tyst fel synligt. Rutan syns bara när något faktiskt fastnat.
      */}
      {health && health.stuckCount > 0 ? (
        <Alert
          style={{ marginBottom: 16 }}
          type="warning"
          showIcon
          message={`${health.stuckCount} adress(er) har fastnat i geokodning`}
          description={
            <span>
              De är markerade som pågående men inget jobb arbetar med dem
              (äldsta {health.oldestMinutes} min, tröskel {health.thresholdMinutes} min).
              Det inträffar om backend startats om mitt i en import. Kör om
              geokodningen för berörd zon — de blir aldrig klara av sig själva.
            </span>
          }
        />
      ) : null}

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <label style={{ fontWeight: 600, fontSize: 12 }}>Meddelande till användarna</label>
          <TextArea
            rows={2}
            value={form.message}
            onChange={(e) => set("message")(e.target.value)}
            placeholder="T.ex. Vi uppdaterar systemet under fredagen."
          />
          <span style={{ fontSize: 11, color: "#888" }}>
            Texten visas precis som du skriver den. Tidsintervallet läggs till
            automatiskt efter meddelandet — skriv det inte själv, då står det dubbelt.
          </span>
        </div>

        <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
          Alla tider anges i svensk tid. De två fönstren är oberoende — bannern
          visas ofta flera dagar innan importerna faktiskt pausas.
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label="Banner visas från" value={form.bannerStart} onChange={set("bannerStart")} />
          <Field label="Banner visas till" value={form.bannerEnd} onChange={set("bannerEnd")} />
          <Field
            label="Importer blockeras från"
            value={form.blockStart}
            onChange={set("blockStart")}
            hint="Pågående jobb avbryts aldrig — bara nya stoppas."
          />
          <Field label="Importer blockeras till" value={form.blockEnd} onChange={set("blockEnd")} />
        </div>

        <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
          <Button type="primary" loading={saving} onClick={save}>
            Spara underhållsfönster
          </Button>
          <Popconfirm
            title="Avsluta underhållet nu?"
            description="Banner och blockering stängs av omedelbart, oavsett sluttiderna."
            okText="Ja, avsluta"
            cancelText="Avbryt"
            onConfirm={cancelNow}
          >
            <Button danger>Avsluta nu</Button>
          </Popconfirm>
        </div>
      </div>
    </div>
  );
};

export default MaintenanceSettings;
